import * as React from "react";
import { sharedSchedule } from "@klapzo/kit-core";
import { sharedCsv, sharedDocxModel } from "../agendaShare.js";
import { buildDocx, DOCX_MIME } from "../agendaDocx.js";
import { getSharedAgenda, type SharedAgenda } from "../agendasApi.js";
import { useRemote } from "../kitApi.js";
import { t } from "../i18n.js";
import { KitShell, RemoteError } from "../components.js";

/**
 * A shared agenda, read-only. Whoever holds the link sees the plan — the response carries no id,
 * tenant, or owner, so there is nothing here to identify the account that made it.
 *
 * An unknown token and a revoked one are the same 404 on purpose: probing a link should not reveal
 * whether it ever existed. Hence one "this link isn't available" page rather than two.
 */
export function SharedAgendaView({ token }: { token: string }): React.ReactElement {
  const remote = useRemote<SharedAgenda | null>((signal) => getSharedAgenda(token, signal), [token]);

  React.useEffect(() => {
    // Belt and braces alongside the API's `X-Robots-Tag: noindex` — a crawler that reaches the SPA
    // route without fetching the API still must not index someone's workshop plan.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (remote.state === "loading") {
    return (
      <KitShell>
        <p>{t("kit.loading")}</p>
      </KitShell>
    );
  }

  if (remote.state === "error") {
    return (
      <KitShell>
        <RemoteError message={t("kit.error")} retry={t("kit.retry")} />
      </KitShell>
    );
  }

  const agenda = remote.data;
  if (!agenda) {
    return (
      <KitShell>
        <h1 className="font-display text-display-md">{t("share.gone")}</h1>
        <p className="text-muted">{t("share.goneHint")}</p>
      </KitShell>
    );
  }

  const scheduled = sharedSchedule(agenda);

  // The visitor's CSV is made from the link's own response, so it carries exactly the blocks the
  // page shows — print/PDF is this same page. The owner's complete exports live in the planner.
  const downloadCsv = (): void => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(
      new Blob(
        [
          sharedCsv(agenda, {
            order: t("planner.csvOrder"),
            start: t("planner.start"),
            title: t("planner.csvBlock"),
            duration: `${t("planner.csvDuration")} (${t("planner.min")})`,
            total: t("planner.total"),
          }),
        ],
        { type: "text/csv;charset=utf-8" },
      ),
    );
    a.download = `${agenda.title.replace(/[\s/\\]+/g, "-").toLowerCase() || "agenda"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Same rule as the CSV: built from the link's own response, so a hidden block cannot be in it.
  const downloadDocx = (): void => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(
      new Blob(
        [
          buildDocx(sharedDocxModel(agenda), {
            order: t("planner.csvOrder"),
            start: t("planner.start"),
            block: t("planner.csvBlock"),
            duration: t("planner.csvDuration"),
            method: t("planner.docxMethod"),
            total: t("planner.total"),
            min: t("planner.min"),
            facilitatorOnly: t("planner.docxFacOnly"),
          }),
        ],
        { type: DOCX_MIME },
      ),
    );
    a.download = `${agenda.title.replace(/[\s/\\]+/g, "-").toLowerCase() || "agenda"}.docx`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <KitShell>
      <h1 className="font-display text-display-md">{agenda.title}</h1>
      <p className="text-body-sm text-muted">{t("share.readOnly")}</p>
      <div className="no-print flex gap-3">
        <button onClick={downloadCsv} className="rounded-md bg-surface px-3 py-1">{t("share.csv")}</button>
        <button onClick={downloadDocx} className="rounded-md bg-surface px-3 py-1">{t("share.docx")}</button>
        <button onClick={() => window.print()} className="rounded-md bg-surface px-3 py-1">{t("share.print")}</button>
      </div>

      <div className="flex justify-between rounded-md bg-surface p-3 font-bold">
        <span>{t("planner.total")}</span>
        <span>
          {scheduled.totalMin} {t("planner.min")}
          {agenda.startClock && ` · ${agenda.startClock}–${scheduled.endClock}`}
        </span>
      </div>

      <ol className="flex flex-col gap-2">
        {scheduled.blocks.map((b) => (
          <li key={b.id} className="flex items-center gap-3 rounded-md border border-border p-3">
            {b.startClock && <span className="w-14 font-bold tabular-nums">{b.startClock}</span>}
            <span className="flex-1">{b.title}</span>
            <span className="text-body-sm text-muted">
              {b.durationMin} {t("planner.min")}
            </span>
          </li>
        ))}
      </ol>
    </KitShell>
  );
}
